import { useState } from 'react';
import { Alert, Button, Code, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import axios from 'axios';
import {
  useCallableContext,
  useCompositionAutomations,
  useCompositionScope,
} from '@/hooks/api/automations/useExecutionCompositions.ts';
import type {
  CallableContext,
  LogicalTarget,
  OutputSource,
  SemanticEdit,
  SemanticOperation,
  StructuralPath,
} from '@/types/executionCompositions.ts';
import ExplicitMappingEditor from './ExplicitMappingEditor.tsx';
import { ImplementationSelect } from './CompositionSelectors.tsx';
import { pathLabel } from './compositionPresentation.ts';

type Props = {
  versionId?: string;
  disabled: boolean;
  initialEdit?: SemanticEdit;
  onEdit: (edit: SemanticEdit | undefined) => void;
  onLogicalSelection?: (
    path: StructuralPath,
    automationId: string,
    versionId: string,
    triggerId: string,
  ) => void;
};
export default function ImplementationEditor(props: Props) {
  const context = useCallableContext(props.versionId);
  const scope = useCompositionScope();
  const [open, setOpen] = useState(false);
  const accessDenied =
    axios.isAxiosError(context.error) &&
    (context.error.response?.status === 401 || context.error.response?.status === 403);
  return (
    <Stack gap="xs">
      <Button variant="subtle" disabled={props.disabled} onClick={() => setOpen((value) => !value)}>
        {open ? 'Hide implementation editing' : 'Edit logic or public interface'}
      </Button>
      {props.initialEdit && (
        <Alert color="orange">
          A real implementation edit is included. Only the included operations will be saved; form
          changes need to be included again. Saving will create a successor version. Exact
          successor-bound requirements need separate review.
        </Alert>
      )}
      {open && context.isError && (
        <Stack gap="xs">
          <Text c="red">Authoritative interface inspection is unavailable.</Text>
          <Button
            variant="subtle"
            disabled={props.disabled || context.isFetching}
            onClick={() => void context.refetch()}
          >
            Retry implementation interface
          </Button>
        </Stack>
      )}
      {open && context.data && !accessDenied && (
        <EditForm
          key={JSON.stringify([
            ...scope,
            context.data.automation_version_id,
            context.data.latest_version_id,
            context.data.published_version_id,
            context.data.head_revision,
            context.data.public_outputs_sha256,
          ])}
          {...props}
          context={context.data}
          available={context.isFetchedAfterMount && !context.isError}
        />
      )}
    </Stack>
  );
}
function EditForm({
  context,
  disabled,
  onEdit,
  onLogicalSelection,
  initialEdit,
  available,
}: Props & { context: CallableContext; available: boolean }) {
  // Included operations belong to the workspace request; only unfinished input is local.
  const operations = initialEdit?.operations ?? [];
  const staleEdit = Boolean(
    initialEdit &&
    (initialEdit.base_version_id !== context.automation_version_id ||
      initialEdit.expected_latest_version_id !== context.latest_version_id ||
      initialEdit.expected_published_version_id !== context.published_version_id ||
      initialEdit.expected_head_revision !== context.head_revision ||
      operations.some(
        (operation) =>
          operation.op_type === 'replace_version_public_outputs' &&
          operation.expected_current_value_sha256 !== context.public_outputs_sha256,
      )),
  );
  const [outputs, setOutputs] = useState<OutputSource[]>(
    (staleEdit
      ? context.public_outputs
      : (operations.find((operation) => operation.op_type === 'replace_version_public_outputs')
          ?.public_outputs ?? context.public_outputs)) ?? [],
  );
  const [port, setPort] = useState('');
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const [outputId, setOutputId] = useState('');
  const [callKey, setCallKey] = useState<string | null>(null);
  const [automationId, setAutomationId] = useState<string | null>(null);
  const [versionId, setVersionId] = useState<string | null>(null);
  const [triggerId, setTriggerId] = useState<string | null>(null);
  const [mappings, setMappings] = useState<LogicalTarget['trigger_input_mappings']>([]);
  const [mode, setMode] = useState<'blocking' | 'async'>('blocking');
  const automations = useCompositionAutomations();
  const target = useCallableContext(versionId ?? undefined);
  const [message, setMessage] = useState('');
  const verifiedTarget = target.isFetchedAfterMount && !target.isError ? target.data : undefined;
  const callable = verifiedTarget?.triggers.find((item) => item.step_id === triggerId)?.interface;
  const selectedCall = context.calls.find((item) => JSON.stringify(item.path) === callKey);
  const include = (next: SemanticOperation[]) => {
    if (disabled || !available || staleEdit) return false;
    if (!context.latest_version_id || context.latest_version_id !== context.automation_version_id) {
      setMessage(
        'Implementation editing requires the latest authored source. Select that source explicitly.',
      );
      return false;
    }
    onEdit({
      base_version_id: context.automation_version_id,
      expected_latest_version_id: context.latest_version_id,
      expected_published_version_id: context.published_version_id,
      expected_head_revision: context.head_revision,
      operations: next,
    });
    setMessage('Implementation edit included; prepare a complete composition to review it.');
    return true;
  };
  // Keep local draft state through a transient read failure without rendering
  // cached source controls or portals until authoritative inspection succeeds.
  if (!available) return null;
  return (
    <Stack gap="xs">
      {staleEdit && (
        <Alert color="orange">
          Included implementation edit belongs to an older source basis. Discard implementation
          edits and review the current source before including further changes.
        </Alert>
      )}
      <Text size="sm">
        Public IDs stay stable across implementations. Output schema, availability and artifact
        contracts are derived by the server from exact sources.
      </Text>
      {outputs.map((item) => (
        <Group key={item.port_id}>
          <Code>{item.port_id}</Code>
          <Text size="sm">
            {pathLabel(item.path)} / {item.source_output_id ?? 'default output'}
          </Text>
          <Button
            disabled={disabled}
            variant="subtle"
            onClick={() => setOutputs((current) => current.filter((other) => other !== item))}
          >
            Remove port {item.port_id}
          </Button>
        </Group>
      ))}
      <TextInput
        label="Stable public output ID"
        value={port}
        onChange={(event) => setPort(event.currentTarget.value)}
        disabled={disabled}
      />
      <Select
        label="Authored source location"
        data={context.sources
          .filter((item) => item.path.every((segment) => !segment.subflow_id))
          .map((item) => ({
            value: JSON.stringify(item.path),
            label: `${item.name} · ${pathLabel(item.path)}`,
          }))}
        value={sourceKey}
        onChange={setSourceKey}
        disabled={disabled}
      />
      <TextInput
        label="Source output ID (empty for default output)"
        value={outputId}
        onChange={(event) => setOutputId(event.currentTarget.value)}
        disabled={disabled}
      />
      <Button
        variant="default"
        disabled={
          disabled || !port.trim() || !sourceKey || outputs.some((item) => item.port_id === port)
        }
        onClick={() => {
          setOutputs((current) => [
            ...current,
            {
              port_id: port,
              path: JSON.parse(sourceKey!) as StructuralPath,
              source_output_id: outputId || null,
            },
          ]);
          setPort('');
        }}
      >
        Add public port
      </Button>
      <Button
        variant="default"
        disabled={disabled || staleEdit}
        onClick={() =>
          include([
            ...operations.filter((item) => item.op_type !== 'replace_version_public_outputs'),
            {
              op_type: 'replace_version_public_outputs',
              public_outputs: outputs,
              expected_current_value_sha256: context.public_outputs_sha256,
            },
          ])
        }
      >
        Include public-interface edit
      </Button>
      {context.calls.length > 0 && (
        <>
          <Select
            label="Call to explicitly author as logical"
            value={callKey}
            data={context.calls.map((item) => ({
              value: JSON.stringify(item.path),
              label: `${item.step.name} · ${pathLabel(item.path)}`,
            }))}
            onChange={(value) => {
              setCallKey(value);
              const call = context.calls.find((item) => JSON.stringify(item.path) === value);
              setAutomationId(call?.step.target_automation_id ?? null);
              setMappings(call?.step.trigger_input_mappings ?? []);
              setMode(call?.step.execution_mode ?? 'blocking');
              setVersionId(null);
              setTriggerId(null);
            }}
            disabled={disabled}
          />
          <Select
            label="Target Automation"
            value={automationId}
            data={
              automations.isFetchedAfterMount && !automations.isError
                ? (automations.data?.pages
                    .flatMap((page) => page.results)
                    .map((item) => ({ value: item.id, label: item.name })) ?? [])
                : []
            }
            onChange={(value) => {
              setAutomationId(value);
              setVersionId(null);
              setTriggerId(null);
            }}
            disabled={disabled || !automations.isFetchedAfterMount || automations.isError}
            searchable
          />
          {automations.isError && (
            <Stack gap="xs">
              <Text c="red">Target Automation discovery unavailable.</Text>
              <Button
                variant="subtle"
                disabled={disabled || automations.isFetching}
                onClick={() =>
                  void (automations.isFetchNextPageError
                    ? automations.fetchNextPage()
                    : automations.refetch())
                }
              >
                Retry target Automation discovery
              </Button>
            </Stack>
          )}
          {automations.hasNextPage && (
            <Button
              variant="subtle"
              disabled={
                disabled ||
                !automations.isFetchedAfterMount ||
                automations.isError ||
                automations.isFetching
              }
              onClick={() => void automations.fetchNextPage()}
            >
              Load more target Automations
            </Button>
          )}
          {automationId && (
            <ImplementationSelect
              automationId={automationId}
              value={versionId}
              onChange={(value) => {
                setVersionId(value);
                setTriggerId(null);
              }}
              disabled={disabled}
            />
          )}
          {target.isError && (
            <Stack gap="xs">
              <Text c="red">Target interface unavailable. Refresh before authoring.</Text>
              <Button
                variant="subtle"
                disabled={disabled || target.isFetching}
                onClick={() => void target.refetch()}
              >
                Retry target interface
              </Button>
            </Stack>
          )}
          <Select
            label="Target callable trigger"
            data={
              verifiedTarget?.triggers
                .filter((item) => item.interface?.trigger_kind === 'manual')
                .map((item) => ({ value: item.step_id, label: item.name })) ?? []
            }
            value={triggerId}
            onChange={setTriggerId}
            disabled={disabled}
          />
          {verifiedTarget && verifiedTarget.public_outputs === null && (
            <Alert color="orange">
              Target needs an explicit public-output declaration before it can be selected for a
              logical call.
            </Alert>
          )}
          <Select
            label="Execution mode"
            data={['blocking', 'async']}
            value={mode}
            disabled={disabled}
            onChange={(value) => setMode(value as 'blocking' | 'async')}
          />
          <Text size="sm">
            Input mappings are explicit. The server checks source scope, availability and
            compatibility.
          </Text>
          {callable && (
            <ExplicitMappingEditor
              inputs={callable.inputs}
              mappings={mappings}
              sources={context.sources}
              onChange={setMappings}
              disabled={disabled}
            />
          )}
          <Button
            variant="default"
            disabled={
              disabled ||
              staleEdit ||
              !selectedCall ||
              !callable ||
              !automationId ||
              !versionId ||
              !onLogicalSelection
            }
            onClick={() => {
              const step = selectedCall!.path.at(-1)!;
              const operation: SemanticOperation = {
                op_type: 'replace_subautomation_target',
                step_id: step.step_id,
                ...(step.subflow_id ? { subflow_id: step.subflow_id } : {}),
                value: {
                  binding_mode: 'composition',
                  target_automation_id: automationId!,
                  target_trigger_step_id: callable!.trigger_step_id,
                  execution_mode: mode,
                  expected_interface: callable!,
                  trigger_input_mappings: mappings.filter((mapping) =>
                    callable!.inputs.some((port) => port.port_id === mapping.trigger_input_id),
                  ),
                },
              };
              const included = include([
                ...operations.filter(
                  (item) =>
                    item.op_type !== 'replace_subautomation_target' ||
                    item.step_id !== step.step_id ||
                    item.subflow_id !== step.subflow_id,
                ),
                operation,
              ]);
              if (included)
                onLogicalSelection!(
                  selectedCall!.path,
                  automationId!,
                  versionId!,
                  callable!.trigger_step_id,
                );
            }}
          >
            Include explicit logical-call edit
          </Button>
        </>
      )}
      {message && <Text>{message}</Text>}
      <Button
        variant="subtle"
        disabled={disabled}
        onClick={() => {
          if (staleEdit) setOutputs(context.public_outputs ?? []);
          onEdit(undefined);
          setMessage('Implementation edit removed.');
        }}
      >
        Discard implementation edits
      </Button>
    </Stack>
  );
}
